import { Head, Link } from "@inertiajs/react"
import { ArrowLeft } from "lucide-react"
import { useTranslation } from "react-i18next"

import { MasterclassModuleForm } from "@/components/masterclasses/masterclass-module-form"
import AppLayout from "@/layouts/app-layout"
import { masterclassPath } from "@/routes"
import type { MasterclassModuleFormProps } from "@/types/masterclasses"

/**
 * OKS-55 — `Brand/Masterclasses/Modules/New`.
 *
 * Full-page New Module form (UX Spec §2.2 — chose full-page over Drawer
 * because direct-upload progress + multiple fields fit poorly in a
 * modal; symmetric with OKS-54's `Brand/Masterclasses/New`).
 *
 * Page chrome — breadcrumb back to the parent masterclass Show + title.
 * The shared `<MasterclassModuleForm>` owns submit / validation /
 * state preview.
 */
export default function MasterclassModulesNew({
  masterclass,
  masterclass_module,
  mode,
  direct_upload_url,
  cap_hard,
}: MasterclassModuleFormProps) {
  const { t } = useTranslation()

  return (
    <AppLayout>
      <Head title={t("masterclasses.modules.new_title")} />
      <div
        className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-6"
        data-testid="masterclass-module-new-page"
      >
        <header className="flex flex-col gap-3">
          <Link
            href={masterclassPath(masterclass.id)}
            className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1 text-sm"
            data-testid="masterclass-module-new-back-link"
          >
            <ArrowLeft className="size-4" aria-hidden="true" />
            {masterclass.title ?? t("masterclasses.modules.breadcrumb_root")}
          </Link>
          <h1 className="text-foreground text-2xl font-semibold tracking-tight">
            {t("masterclasses.modules.new_title")}
          </h1>
        </header>

        <MasterclassModuleForm
          masterclass={masterclass}
          module={masterclass_module}
          mode={mode}
          directUploadUrl={direct_upload_url}
          capHard={cap_hard}
        />
      </div>
    </AppLayout>
  )
}
